import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";

import ActorSprite from "../sprites/actor-sprite";
import RecordingHandle, { RecordingHandleSide } from "../sprites/recording-handle";
import RecordingIgnoredSprite from "../sprites/recording-ignored-sprite";
import RecordingMaskSprite from "../sprites/recording-mask-sprite";
import { RecordingSquareStatus } from "../sprites/recording-square-status";
import { DEFAULT_APPEARANCE_INFO, SPRITE_TRANSFORM_CSS } from "../sprites/sprite";
import ActorSelectionPopover from "./actor-selection-popover";

import { createCharacter } from "../../actions/characters-actions";
import {
  setRecordingExtent,
  setupRecordingForActor,
  toggleSquareIgnored,
  upsertRecordingCondition,
} from "../../actions/recording-actions";
import {
  changeActors,
  changeActorsIndividually,
  createActors,
  deleteActors,
  recordInputForGameState,
} from "../../actions/stage-actions";
import {
  paintCharacterAppearance,
  select,
  selectToolId,
  selectToolItem,
} from "../../actions/ui-actions";

import { STAGE_CELL_SIZE, TOOLS } from "../../constants/constants";
import {
  computeDoorDefaultDestination,
  DOOR_VARIABLE_IDS,
  IncomingDoorDestination,
} from "../../utils/door-constants";
import {
  getStageBackground,
  getStageHeight,
  getStageTileSize,
  getStageWidth,
} from "../../utils/builtin-stage-variables";
import { extentIgnoredPositions } from "../../utils/recording-helpers";
import {
  actorFilledPoints,
  actorFillsPoint,
  actorsAtPoint,
  applyAnchorAdjustment,
  buildActorSelection,
  getVariableValue,
  pointIsOutside,
  sortActorsByZOrder,
  stagePositionsBetween,
  stageSquareForPixelOffset,
} from "../../utils/stage-helpers";

import { useEditorSelector } from "../../../hooks/redux";
import { isFullscreenShortcut } from "../../../hooks/useFullscreen";
import {
  Actor,
  Character,
  EvaluatedSquare,
  Position,
  RuleExtent,
  Stage as StageType,
  WorldMinimal,
} from "../../../types";
import { defaultAppearanceId } from "../../utils/character-helpers";
import { canonicalKey } from "../../utils/keys";
import { makeId } from "../../utils/utils";
import {
  heldKeysAsInput,
  holdKeys,
  KeySource,
  releaseKeys,
  releaseSource,
} from "../../utils/held-keys";

interface StageProps {
  stage: StageType;
  world: WorldMinimal;
  recordingExtent?: RuleExtent;
  recordingCentered?: boolean;
  evaluatedSquares?: EvaluatedSquare[];
  /** Controls how users can interact with actors on the stage:
   * - "full": All interactions (click, drag, selection rect, tools). Default.
   * - "selectable": Click/select actors but no dragging (used in player).
   */
  interactionMode?: "full" | "selectable";
  immersive?: boolean;
  animatingTick?: boolean;
  style?: CSSProperties;
  /**
   * Door actors on other stages whose destination points to this stage.
   * Rendered as read-only indicators so users can see incoming teleports
   * without navigating back to the source stage.
   */
  doorsPointingHere?: IncomingDoorDestination[];
}

type Offset = { top: string | number; left: string | number };
type MouseStatus = {
  isDown: boolean;
  visited: { [posKey: string]: true };
  lastPosition: Position | null;
};
type SelectionRect = { start: { top: number; left: number }; end: { top: number; left: number } };

// Custom drag state for multi-sprite dragging with proper transform preview
type SpriteDragState = {
  actors: Actor[]; // All actors being dragged
  anchorActorId: string; // The actor that was clicked to start the drag
  anchorOffset: { x: number; y: number }; // Click offset within the anchor actor (in scaled px)
  clientPx: { x: number; y: number }; // Current viewport coordinates for drag preview
  mode: "move" | "copy"; // Whether we're moving or copying (alt key)
};

// Tools that act on whatever you press. Actors stay undraggable while one is
// selected, so a wobbly press can't pick a character up instead of using it.
const NON_DRAGGING_TOOLS = [
  TOOLS.IGNORE_SQUARE,
  TOOLS.TRASH,
  TOOLS.STAMP,
  TOOLS.CREATE_CHARACTER,
  TOOLS.RECORD,
  TOOLS.PAINT,
  TOOLS.ADD_CLICK_CONDITION,
];

// Single empty image used for hiding native drag preview
// eslint-disable-next-line react-refresh/only-export-components
export const EMPTY_DRAG_IMAGE = new Image();
EMPTY_DRAG_IMAGE.src =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// Reads a Stage's scale settings. Tile size (in pixels) lives in
// `variableValues.tileSize`; the returned `tileScale` is the multiplier
// applied to STAGE_CELL_SIZE in the renderer.
// eslint-disable-next-line react-refresh/only-export-components
export function resolveStageScaleSettings(stage: {
  variableValues: Record<string, string>;
  zoomToFill?: boolean;
  zoomToFit?: boolean;
}): { tileScale: number; zoomToFill: boolean; zoomToFit: boolean } {
  const tilePx = getStageTileSize(stage);
  return {
    tileScale: tilePx / STAGE_CELL_SIZE,
    zoomToFill: stage.zoomToFill ?? true,
    zoomToFit: stage.zoomToFit ?? false,
  };
}

const SpriteDragPreview = ({
  spriteDrag,
  characters,
  scale,
}: {
  spriteDrag: SpriteDragState;
  characters: { [id: string]: Character };
  scale: number;
}) => {
  const anchorActor = spriteDrag.actors.find((a) => a.id === spriteDrag.anchorActorId);
  if (!anchorActor) return null;

  // Base position: cursor position minus the click offset within the anchor sprite
  const baseX = spriteDrag.clientPx.x - spriteDrag.anchorOffset.x;
  const baseY = spriteDrag.clientPx.y - spriteDrag.anchorOffset.y;

  return (
    <div
      className="sprite-drag-preview"
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        pointerEvents: "none",
        zIndex: 10000,
      }}
    >
      {spriteDrag.actors.map((actor) => {
        const character = characters[actor.characterId];
        if (!character) return null;
        const { appearances, appearanceInfo } = character.spritesheet;
        const info = appearanceInfo?.[actor.appearance] || DEFAULT_APPEARANCE_INFO;
        const data = appearances[actor.appearance]?.[0];
        if (!data) return null;

        // Position relative to anchor actor (in grid cells, converted to pixels)
        const relX = actor.position.x - anchorActor.position.x;
        const relY = actor.position.y - anchorActor.position.y;

        const transform = actor.transform ?? "0";

        return (
          <img
            key={actor.id}
            src={data}
            className="sprite"
            style={{
              position: "absolute",
              left: baseX + (relX - info.anchor.x) * STAGE_CELL_SIZE * scale,
              // Y-up world: relY > 0 means the actor is higher visually than
              // the anchor. baseY anchors the cursor click point, so we
              // subtract upward distance and add the sprite-pixel anchor
              // offset (sprite-pixel space stays Y-down).
              top: baseY + (-relY - info.anchor.y) * STAGE_CELL_SIZE * scale,
              width: info.width * STAGE_CELL_SIZE * scale,
              height: info.height * STAGE_CELL_SIZE * scale,
              transform: SPRITE_TRANSFORM_CSS[transform],
              transformOrigin: `${((info.anchor.x + 0.5) / info.width) * 100}% ${((info.anchor.y + 0.5) / info.height) * 100}%`,
              opacity: 0.85,
              filter:
                spriteDrag.mode === "copy" ? "drop-shadow(2px 2px 4px rgba(0,0,0,0.3))" : undefined,
            }}
          />
        );
      })}
    </div>
  );
};

/**
 * Feeds the keys the player is holding into the world's frame input. Holds are
 * recorded in the shared held-keys module so the playback loop can put them
 * back into the input each tick clears - without that, a held key would only
 * ever fire once. See utils/held-keys.
 */
function useGlobalHeldKeys(worldId: string, playbackRunning: boolean) {
  const dispatch = useDispatch();
  // Identifies this handler's holds among the other live sources.
  const source = useRef<KeySource>({}).current;
  const playbackRunningRef = useRef(playbackRunning);
  playbackRunningRef.current = playbackRunning;

  useEffect(() => {
    if (!worldId) {
      return;
    }
    const syncHeldKeys = () => {
      dispatch(recordInputForGameState(worldId, { keys: heldKeysAsInput() }));
    };

    const onDocumentKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        return;
      }

      if (isFullscreenShortcut(event)) {
        return;
      }

      // Auto-repeat fires keydown over and over for a key that's already down,
      // so only the first one is worth dispatching.
      if (holdKeys(source, [canonicalKey(event.key)])) {
        syncHeldKeys();
      }
    };

    const onDocumentKeyUp = (event: KeyboardEvent) => {
      if (releaseKeys(source, [canonicalKey(event.key)])) {
        // When playing, don't sync on keyup - let the key persist until tick() clears it.
        // This ensures quick key taps are registered even if keyup happens before next tick.
        // (The next tick won't put it back, because it's no longer held.)
        // When stopped, sync immediately so Forward button sees current held state.
        if (!playbackRunningRef.current) {
          syncHeldKeys();
        }
      }
    };

    const onWindowBlur = () => {
      if (releaseSource(source)) {
        syncHeldKeys();
      }
    };

    document.addEventListener("keydown", onDocumentKeyDown);
    document.addEventListener("keyup", onDocumentKeyUp);
    window.addEventListener("blur", onWindowBlur);

    return () => {
      document.removeEventListener("keydown", onDocumentKeyDown);
      document.removeEventListener("keyup", onDocumentKeyUp);
      window.removeEventListener("blur", onWindowBlur);
      // The held-keys module outlives this component. Navigating away with a
      // key still down fires no blur, and the keyup arrives after these
      // listeners are gone - so let go here, or the key stays "held" and
      // drives the next stage that mounts.
      releaseSource(source);
    };
  }, [dispatch, source, worldId]);
}

export const Stage = ({
  recordingExtent,
  recordingCentered,
  evaluatedSquares,
  stage,
  world,
  interactionMode = "full",
  style,
  animatingTick,
  doorsPointingHere,
}: StageProps) => {
  const stageWidth = getStageWidth(stage);
  const stageHeight = getStageHeight(stage);
  const [{ top, left }, setOffset] = useState<Offset>({ top: 0, left: 0 });
  const [scale, setScale] = useState(() => resolveStageScaleSettings(stage).tileScale);

  const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null);
  const [actorSelectionPopover, setActorSelectionPopover] = useState<{
    actors: Actor[];
    position: { x: number; y: number };
    toolId: string;
  } | null>(null);
  const [spriteDrag, setSpriteDrag] = useState<SpriteDragState | null>(null);
  const [doorDestDrag, setDoorDestDrag] = useState<{
    actorId: string;
    position: Position;
  } | null>(null);

  const lastFiredExtent = useRef<string | null>(null);
  const lastActorPositions = useRef<{ [actorId: string]: Position }>({});
  // Centering offset: when the stage is smaller than the scroll wrap (fit/immersive mode),
  // it is centered via CSS flex. This state tracks that offset so the selection box
  // (which is position:absolute in the scroll wrap) can be positioned correctly.
  const [centeringOffset, setCenteringOffset] = useState({ left: 0, top: 0 });

  const mouse = useRef<MouseStatus>({ isDown: false, visited: {}, lastPosition: null });
  const scrollEl = useRef<HTMLDivElement | null>();
  const el = useRef<HTMLDivElement | null>();

  // Removers for document listeners attached during an in-progress interaction,
  // so a drag that never ends (eg. the user navigates away mid-drag) doesn't
  // leave handlers running against an unmounted stage.
  const pendingInteractionCleanups = useRef(new Set<() => void>());

  useEffect(() => {
    const cleanups = pendingInteractionCleanups.current;
    return () => {
      cleanups.forEach((cleanup) => cleanup());
      cleanups.clear();
    };
  }, []);

  // Attaches document listeners and returns a function that removes them. Call it
  // when the interaction ends; anything still pending is removed on unmount.
  const addInteractionListeners = (listeners: {
    [K in keyof DocumentEventMap]?: (event: DocumentEventMap[K]) => void;
  }) => {
    const entries = Object.entries(listeners) as [string, EventListener][];
    entries.forEach(([type, handler]) => document.addEventListener(type, handler));

    const remove = () => {
      entries.forEach(([type, handler]) => document.removeEventListener(type, handler));
      pendingInteractionCleanups.current.delete(remove);
    };
    pendingInteractionCleanups.current.add(remove);
    return remove;
  };

  useEffect(() => {
    const autofit = () => {
      const _scrollEl = scrollEl.current;
      const _el = el.current;
      if (!_scrollEl || !_el) {
        return;
      }
      if (recordingCentered) {
        setScale(1);
        setCenteringOffset({ left: 0, top: 0 });
        _scrollEl.style.justifyContent = "";
        _scrollEl.style.alignItems = "";
        return;
      }

      _el.style.zoom = "1"; // this needs to be here for scaling "up" to work
      const { tileScale, zoomToFill, zoomToFit } = resolveStageScaleSettings(stage);
      const fit = Math.min(
        _scrollEl.clientWidth / (stageWidth * STAGE_CELL_SIZE),
        _scrollEl.clientHeight / (stageHeight * STAGE_CELL_SIZE),
      );
      // Start at the configured tile size, then optionally scale up (fill) or
      // down (fit) based on how the stage compares to the viewport.
      let best = tileScale;
      if (fit > tileScale && zoomToFill) best = fit;
      if (fit < tileScale && zoomToFit) best = fit;
      _el.style.zoom = `${best}`;
      setScale(best);

      // Disable centering only on axes that overflow so the stage remains centered
      // on any axis that still fits within the viewport.
      const stageW = stageWidth * STAGE_CELL_SIZE * best;
      const stageH = stageHeight * STAGE_CELL_SIZE * best;
      const overflowsX = stageW > _scrollEl.clientWidth;
      const overflowsY = stageH > _scrollEl.clientHeight;
      _scrollEl.style.justifyContent = overflowsX ? "flex-start" : "";
      _scrollEl.style.alignItems = overflowsY ? "flex-start" : "";
      setCenteringOffset({
        left: overflowsX ? 0 : Math.max(0, (_scrollEl.clientWidth - stageW) / 2),
        top: overflowsY ? 0 : Math.max(0, (_scrollEl.clientHeight - stageH) / 2),
      });
    };

    // ResizeObserver fires during CSS transitions (e.g. split-stage close animation),
    // ensuring the zoom is recalculated as the container grows back to full width.
    const observer = new ResizeObserver(autofit);
    if (scrollEl.current) observer.observe(scrollEl.current);

    window.addEventListener("resize", autofit);
    document.addEventListener("fullscreenchange", autofit);
    autofit();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", autofit);
      document.removeEventListener("fullscreenchange", autofit);
    };
  }, [
    stage,
    stageHeight,
    stageWidth,
    recordingCentered,
  ]);

  const dispatch = useDispatch();
  const characters = useEditorSelector((state) => state.characters);
  const characterZOrder = useEditorSelector((state) => state.characterZOrder);
  const { selectedActors, selectedToolId, stampToolItem, playback } = useEditorSelector(
    (state) => ({
      selectedActors: state.ui.selectedActors,
      selectedToolId: state.ui.selectedToolId,
      stampToolItem: state.ui.stampToolItem,
      playback: state.ui.playback,
    }),
  );

  useGlobalHeldKeys(world.id, playback.running);

  // When playback stops, remount the actor sprites so any in-flight CSS
  // transitions snap straight to their final positions.
  const [stoppedGeneration, setStoppedGeneration] = useState(0);
  const wasRunningRef = useRef(playback.running);
  useEffect(() => {
    if (wasRunningRef.current && !playback.running) {
      setStoppedGeneration((g) => g + 1);
    }
    wasRunningRef.current = playback.running;
  }, [playback.running]);

  // Helpers

  const selFor = (actorIds: string[]) => {
    return buildActorSelection(world.id, stage.id, actorIds);
  };

  const selected =
    selectedActors && selectedActors?.worldId === world.id && selectedActors?.stageId === stage.id
      ? selectedActors.actorIds.map((a) => stage.actors[a]).filter(Boolean)
      : [];

  const centerOnExtent = () => {
    if (!recordingExtent) {
      return { left: 0, top: 0 };
    }
    const { xmin, ymin, xmax, ymax } = recordingExtent;
    // 1-indexed cell at column k has its visual center at screen-from-left
    // = (k - 0.5) cells, and at row k has its visual center at
    // screen-from-top = (stageHeight - k + 0.5) cells.
    const xCenterScreen = (xmin + xmax) / 2 - 0.5;
    const yCenterScreen = stageHeight - (ymin + ymax) / 2 + 0.5;

    // The flex container always positions the stage at (containerSize - stageSize) / 2
    // (which is negative when the stage overflows). The container dimensions cancel out:
    //   naturalEdge + offset + centerScreen * cellPx = containerSize / 2
    //   (containerSize - stageSize) / 2 + offset + centerScreen * cellPx = containerSize / 2
    //   offset = stageSize / 2 - centerScreen * cellPx
    const cellPx = STAGE_CELL_SIZE * scale;
    return {
      left: (stageWidth / 2 - xCenterScreen) * cellPx,
      top: (stageHeight / 2 - yCenterScreen) * cellPx,
    };
  };

  const centerOnActor = useCallback(
    (actorId: string): Offset | null => {
      if (!actorId) return null;

      const actor = stage.actors[actorId];
      if (!actor) return null;

      // 1-indexed Y-up: cell at world (x, y) has its center at
      // (x - 0.5, stageHeight - y + 0.5) cells from the stage div's top-left.
      const xCenterScreen = actor.position.x - 0.5;
      const yCenterScreen = stageHeight - actor.position.y + 0.5;

      return {
        left: `calc(-${xCenterScreen * STAGE_CELL_SIZE}px + 50%)`,
        top: `calc(-${yCenterScreen * STAGE_CELL_SIZE}px + 50%)`,
      };
    },
    [stage.actors, stageHeight],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (playback.running && el.current) {
      el.current.focus();
    }

    let offset: Offset = { top: 0, left: 0 };
    if (recordingExtent && recordingCentered) {
      offset = centerOnExtent();
    }

    if (top !== offset.top || left !== offset.left) {
      setOffset(offset);
    }
  });

  // Camera follow effect - center on followed actor after each tick during playback
  useEffect(() => {
    if (!playback.running) return;
    if (recordingExtent && recordingCentered) return; // Don't override recording centering

    const cameraFollowId = world.globals?.cameraFollow?.value;
    if (!cameraFollowId) return;

    // Check if stage is overflowing (needs scrolling)
    const scrollWrapper = scrollEl.current;
    if (!scrollWrapper) return;

    const stagePxWidth = stageWidth * STAGE_CELL_SIZE * scale;
    const stagePxHeight = stageHeight * STAGE_CELL_SIZE * scale;
    const isOverflowing =
      stagePxWidth > scrollWrapper.clientWidth || stagePxHeight > scrollWrapper.clientHeight;

    if (!isOverflowing) return;

    const newOffset = centerOnActor(cameraFollowId);
    if (newOffset) {
      setOffset(newOffset);
    }
  }, [
    playback.running,
    centerOnActor,
    stageWidth,
    stageHeight,
    world.globals?.cameraFollow?.value,
    scale,
    recordingExtent,
    recordingCentered,
  ]);

  const onBlur = () => {
    if (playback.running) {
      el.current?.focus();
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const isShortcut = event.metaKey || event.ctrlKey;

    if (isShortcut && event.key === "a") {
      dispatch(select(null, selFor(Object.keys(stage.actors))));
      event.preventDefault();
      return;
    }
    if (event.shiftKey || isShortcut) {
      // do not catch these events, they're probably actual hotkeys
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.key === "Delete" || event.key === "Backspace") {
      if (selected.length) {
        dispatch(deleteActors(selFor(selected.map((a) => a.id))));
      }
      return;
    }
    // Note: Key tracking for game state is handled by document-level listeners
    // in the useEffect above, so we don't need to dispatch here
  };

  const onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const onDrop = (event: React.DragEvent) => {
    if (event.dataTransfer.types.includes("sprite")) {
      onDropSprite(event);
      // Clear drag preview and popover immediately on drop, before React re-renders.
      // We can't rely on dragend because if didWrap triggers a key change,
      // the source element unmounts before dragend fires.
      setSpriteDrag(null);
      setActorSelectionPopover(null);
    }
    if (event.dataTransfer.types.includes("appearance")) {
      onDropAppearance(event);
    }
  };

  const onUpdateHandle = (side: RecordingHandleSide, clientX: number, clientY: number) => {
    if (!el.current) {
      return;
    }
    const stageOffset = el.current.getBoundingClientRect();
    // Cell-fractional position. X is 1-indexed cell-from-left;
    // worldY is 1-indexed cell-from-bottom (Y-up).
    const fracX = (clientX - stageOffset.left) / STAGE_CELL_SIZE + 1;
    const worldY = stageHeight - (clientY - stageOffset.top) / STAGE_CELL_SIZE;

    // expand the extent of the recording rule to reflect this new extent
    const nextExtent = Object.assign({}, recordingExtent);
    // clientX/clientY are where the handle's center should be. Each side rounds
    // so the extent is stable while the center stays within half a cell of rest.
    if (side === "left") {
      // Handle sits at x = xmin - 1, center at fracX = xmin - 0.5.
      nextExtent.xmin = Math.min(nextExtent.xmax, Math.max(1, Math.round(fracX + 0.5)));
    }
    if (side === "right") {
      // Handle sits at x = xmax + 1, center at fracX = xmax + 1.5.
      nextExtent.xmax = Math.max(nextExtent.xmin, Math.min(stageWidth, Math.round(fracX - 1.5)));
    }
    if (side === "top") {
      // Visually-top handle drags the topmost row of the extent (ymax in Y-up).
      // Handle sits at world y = ymax + 1, so its center hovers at
      // worldY = ymax + 0.5; round(worldY - 0.5) = ymax keeps it stable
      // at rest and grows the extent as the cursor moves up.
      nextExtent.ymax = Math.max(nextExtent.ymin, Math.min(stageHeight, Math.round(worldY - 0.5)));
    }
    if (side === "bottom") {
      // Visually-bottom handle drags the bottommost row of the extent.
      // Handle sits at world y = ymin - 1, center hovers at worldY = ymin - 1.5;
      // round(worldY + 1.5) = ymin keeps it stable at rest.
      nextExtent.ymin = Math.min(nextExtent.ymax, Math.max(1, Math.round(worldY + 1.5)));
    }

    const str = JSON.stringify(nextExtent);
    if (lastFiredExtent.current === str) {
      return;
    }
    lastFiredExtent.current = str;
    dispatch(setRecordingExtent(nextExtent));
  };

  // Returns null if the stage has left the DOM — document handlers can outlive it.
  const getPxOffsetForEvent = (event: MouseEvent | React.MouseEvent | React.DragEvent) => {
    if (!el.current) {
      return null;
    }
    const stageOffset = el.current.getBoundingClientRect();
    return { left: event.clientX - stageOffset.left, top: event.clientY - stageOffset.top };
  };

  const getPositionForEvent = (event: MouseEvent | React.MouseEvent | React.DragEvent) => {
    const dragOffset =
      "dataTransfer" in event && event.dataTransfer && event.dataTransfer.getData("drag-offset");

    const px = getPxOffsetForEvent(event);
    if (!px) {
      return null;
    }
    return stageSquareForPixelOffset(px, {
      scale,
      stageHeight,
      dragOffset: dragOffset ? JSON.parse(dragOffset) : undefined,
    });
  };

  const onDropAppearance = (event: React.DragEvent) => {
    const { appearance, characterId } = JSON.parse(event.dataTransfer.getData("appearance"));
    // An appearance goes onto whichever actor is under the pointer. Unlike a
    // sprite drop there's no dragged actor to snap into a square, and the drag
    // offset is measured from the library tile, which is bigger than the sprite.
    const px = getPxOffsetForEvent(event);
    if (!px) {
      return;
    }
    const position = stageSquareForPixelOffset(px, { scale, stageHeight });
    if (recordingExtent && pointIsOutside(position, recordingExtent)) {
      return;
    }
    const actor = Object.values(stage.actors).find(
      (a) => actorFillsPoint(a, characters, position) && a.characterId === characterId,
    );
    if (actor) {
      dispatch(changeActors(selFor([actor.id]), { appearance }));
    }
  };

  const onDropActorsAtPosition = (
    { actorIds, dragAnchorActorId }: { actorIds: string[]; dragAnchorActorId: string },
    position: Position,
    mode: "stamp-copy" | "move",
  ) => {
    if (recordingExtent && pointIsOutside(position, recordingExtent)) {
      return;
    }

    const anchorActor = stage.actors[dragAnchorActorId];
    const anchorCharacter = characters[anchorActor.characterId];

    applyAnchorAdjustment(position, anchorCharacter, anchorActor);

    const offsetX = position.x - anchorActor.position.x;
    const offsetY = position.y - anchorActor.position.y;

    if (offsetX === 0 && offsetY === 0) {
      // attempting to drop in the same place we started the drag, don't do anything
      return;
    }

    if (mode === "stamp-copy") {
      const creates = actorIds
        .map((aid) => {
          const actor = stage.actors[aid];
          const character = characters[actor.characterId];
          const clonedActor = Object.assign({}, actor, {
            position: {
              x: actor.position.x + offsetX,
              y: actor.position.y + offsetY,
            },
          });
          const clonedActorPoints = actorFilledPoints(clonedActor, characters).map(
            (p) => `${p.x},${p.y}`,
          );

          // If there is an exact copy of this actor that overlaps this position already, don't
          // drop. It's probably a mistake, and you can override by dropping elsewhere and then
          // dragging it to this square.
          const positionContainsCloneAlready = Object.values(stage.actors).find(
            (a) =>
              a.characterId === actor.characterId &&
              a.appearance === actor.appearance &&
              actorFilledPoints(a, characters).some((p) =>
                clonedActorPoints.includes(`${p.x},${p.y}`),
              ),
          );
          if (positionContainsCloneAlready) {
            return;
          }
          return { character, initialValues: clonedActor };
        })
        .filter((c): c is NonNullable<typeof c> => !!c);

      dispatch(createActors(world.id, stage.id, creates));
    } else if (mode === "move") {
      const upserts = actorIds.map((aid) => ({
        id: aid,
        values: {
          position: {
            x: stage.actors[aid].position.x + offsetX,
            y: stage.actors[aid].position.y + offsetY,
          },
        },
      }));
      dispatch(changeActorsIndividually(world.id, stage.id, upserts));
    } else {
      throw new Error("Invalid mode");
    }
  };

  const onDropCharacterAtPosition = (
    { characterId, appearanceId }: { characterId: string; appearanceId?: string },
    position: Position,
  ) => {
    if (recordingExtent && pointIsOutside(position, recordingExtent)) {
      return;
    }

    const character = characters[characterId];
    const appearance = appearanceId ?? defaultAppearanceId(character.spritesheet);
    const newActor = { position, appearance } as Actor;
    applyAnchorAdjustment(position, character, newActor);

    const newActorPoints = actorFilledPoints(newActor, characters).map((p) => `${p.x},${p.y}`);

    const positionContainsCloneAlready = Object.values(stage.actors).find(
      (a) =>
        a.characterId === characterId &&
        actorFilledPoints(a, characters).some((p) => newActorPoints.includes(`${p.x},${p.y}`)),
    );
    if (positionContainsCloneAlready) {
      return;
    }

    if (character.kind === "door") {
      const dest = computeDoorDefaultDestination(newActor.position, stageWidth);
      newActor.variableValues = {
        ...(newActor.variableValues ?? {}),
        [DOOR_VARIABLE_IDS.destinationX]: String(dest.x),
        [DOOR_VARIABLE_IDS.destinationY]: String(dest.y),
      };
    }

    dispatch(createActors(world.id, stage.id, [{ character, initialValues: newActor }]));
  };

  const onDropSprite = (event: React.DragEvent) => {
    const ids: { actorIds: string[]; dragAnchorActorId: string } | { characterId: string } =
      JSON.parse(event.dataTransfer.getData("sprite"));
    const position = getPositionForEvent(event);
    if (!position) {
      return;
    }
    if ("actorIds" in ids) {
      onDropActorsAtPosition(ids, position, event.altKey ? "stamp-copy" : "move");
    } else if (ids.characterId) {
      onDropCharacterAtPosition(ids, position);
    }
  };

  const onStampAtPosition = (position: Position) => {
    const item = stampToolItem;
    if (item && "actorIds" in item && item.actorIds) {
      const ids = { actorIds: item.actorIds, dragAnchorActorId: item.actorIds[0] };
      onDropActorsAtPosition(ids, position, "stamp-copy");
    } else if (item && "characterId" in item) {
      onDropCharacterAtPosition(item, position);
    }
  };

  // Start custom sprite drag preview (called from ActorSprite onDragStart)
  // Native drag handles dataTransfer, we just show custom preview
  const onStartSpriteDrag = (
    actor: Actor,
    actorIds: string[],
    event: React.DragEvent,
    anchorOffset: { x: number; y: number },
  ) => {
    setSpriteDrag({
      actors: actorIds.map((id) => stage.actors[id]).filter(Boolean),
      anchorActorId: actor.id,
      anchorOffset,
      clientPx: { x: event.clientX, y: event.clientY },
      mode: event.altKey ? "copy" : "move",
    });

    // Track drag position globally via native drag events
    const removeListeners = addInteractionListeners({
      dragover: (e) => {
        setSpriteDrag((prev) =>
          prev
            ? { ...prev, clientPx: { x: e.clientX, y: e.clientY }, mode: e.altKey ? "copy" : "move" }
            : null,
        );
      },
      dragend: () => {
        removeListeners();
        setSpriteDrag(null);
        setActorSelectionPopover(null);
      },
    });
  };

  const onMouseUpActor = (actor: Actor, event: React.MouseEvent) => {
    let handled = false;

    // Helper to check for overlapping actors and show popover if needed
    const clickedPosition = getPositionForEvent(event);
    if (!clickedPosition) {
      return;
    }
    const showPopoverIfOverlapping = (toolId: string): boolean => {
      const overlapping = actorsAtPoint(stage.actors, characters, clickedPosition, characterZOrder);
      if (overlapping.length > 1) {
        setActorSelectionPopover({
          actors: overlapping,
          position: { x: event.clientX, y: event.clientY },
          toolId,
        });
        return true;
      }
      return false;
    };

    switch (selectedToolId) {
      case TOOLS.PAINT:
        if (!showPopoverIfOverlapping(TOOLS.PAINT)) {
          dispatch(paintCharacterAppearance(actor.characterId, actor.appearance));
          dispatch(selectToolId(TOOLS.POINTER));
        }
        handled = true;
        break;
      case TOOLS.STAMP:
        if (!stampToolItem) {
          if (!showPopoverIfOverlapping(TOOLS.STAMP)) {
            dispatch(selectToolItem(selFor([actor.id])));
          }
          handled = true;
        }
        break;
      case TOOLS.RECORD:
        if (!showPopoverIfOverlapping(TOOLS.RECORD)) {
          dispatch(setupRecordingForActor({ characterId: actor.characterId, actor }));
          dispatch(selectToolId(TOOLS.POINTER));
        }
        handled = true;
        break;
      case TOOLS.ADD_CLICK_CONDITION:
        if (!showPopoverIfOverlapping(TOOLS.ADD_CLICK_CONDITION)) {
          dispatch(
            upsertRecordingCondition({
              key: makeId("condition"),
              left: { globalId: "click" },
              right: { constant: actor.id },
              comparator: "=",
              enabled: true,
            }),
          );
          dispatch(selectToolId(TOOLS.POINTER));
        }
        handled = true;
        break;
      case TOOLS.POINTER:
        if (playback.running) {
          dispatch(recordInputForGameState(world.id, { clicks: { [actor.id]: true } }));
        } else if (event.shiftKey) {
          const selectedIds = selected.map((a) => a.id);
          dispatch(
            select(
              actor.characterId,
              selFor(
                selectedIds.includes(actor.id)
                  ? selectedIds.filter((a) => a !== actor.id)
                  : [...selectedIds, actor.id],
              ),
            ),
          );
        } else {
          const overlapping = actorsAtPoint(
            stage.actors,
            characters,
            clickedPosition,
            characterZOrder,
          );
          const topActor = overlapping[overlapping.length - 1];
          const isTopActorSelected = topActor && selected.some((a) => a.id === topActor.id);

          if (overlapping.length > 1 && isTopActorSelected) {
            setActorSelectionPopover({
              actors: overlapping,
              position: { x: event.clientX, y: event.clientY },
              toolId: TOOLS.POINTER,
            });
          } else if (topActor) {
            dispatch(select(topActor.characterId, selFor([topActor.id])));
          }
        }
        handled = true;
        break;
    }

    // If we didn't handle the event, let it bubble up to the stage onClick handler

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onMouseDown = (event: React.MouseEvent) => {
    if (playback.running) {
      return;
    }
    if (interactionMode === "selectable" && selectedToolId !== TOOLS.IGNORE_SQUARE) {
      return;
    }
    const removeListeners = addInteractionListeners({
      mouseup: (e) => {
        removeListeners();
        onMouseUp.current?.(e);
      },
      mousemove: (e) => {
        onMouseMove.current?.(e);
      },
    });
    mouse.current = { isDown: true, visited: {}, lastPosition: null };

    const isClickOnBackground = event.target === event.currentTarget;
    const px =
      selectedToolId === TOOLS.POINTER && isClickOnBackground ? getPxOffsetForEvent(event) : null;
    if (px) {
      setSelectionRect({ start: { ...px }, end: { ...px } });
    } else {
      setSelectionRect(null);
    }

    if (selectedToolId === TOOLS.IGNORE_SQUARE) {
      onMouseMove.current?.(event.nativeEvent);
    }
  };

  // Note: In this handler, the mouse cursor may be outside the stage
  const onMouseMove = useRef<(event: MouseEvent) => void>();
  onMouseMove.current = (event: MouseEvent) => {
    if (!mouse.current.isDown) {
      return;
    }

    // If we are dragging to select a region, update the region.
    // Otherwise, process this event as a tool stroke.
    if (selectionRect) {
      const px = getPxOffsetForEvent(event);
      if (px) {
        setSelectionRect({ ...selectionRect, end: px });
      }
      return;
    }

    const position = getPositionForEvent(event);
    if (!position) {
      return;
    }
    const { x, y } = position;
    if (!(x >= 1 && x <= stageWidth && y >= 1 && y <= stageHeight)) {
      return;
    }

    if (selectedToolId === TOOLS.IGNORE_SQUARE) {
      const from = mouse.current.lastPosition ?? position;
      mouse.current.lastPosition = position;
      for (const square of stagePositionsBetween(from, position)) {
        const posKey = `${square.x},${square.y}`;
        if (!mouse.current.visited[posKey]) {
          mouse.current.visited[posKey] = true;
          dispatch(toggleSquareIgnored(square));
        }
      }
      return;
    }

    const posKey = `${x},${y}`;
    if (mouse.current.visited[posKey]) {
      return;
    }
    mouse.current.visited[posKey] = true;

    if (selectedToolId === TOOLS.STAMP) {
      onStampAtPosition({ x, y });
    }
    if (selectedToolId === TOOLS.TRASH) {
      // If popover is open, skip - we're waiting for user to pick from the popover
      if (actorSelectionPopover) {
        return;
      }

      const overlapping = actorsAtPoint(stage.actors, characters, { x, y }, characterZOrder);

      // On initial click (not drag), show popover if multiple actors overlap
      const isFirstClick = Object.keys(mouse.current.visited).length === 1;
      if (isFirstClick && overlapping.length > 1) {
        setActorSelectionPopover({
          actors: overlapping,
          position: { x: event.clientX, y: event.clientY },
          toolId: TOOLS.TRASH,
        });
        return;
      }

      // For drag or single actor, delete the topmost actor
      const actor = overlapping[overlapping.length - 1];
      if (actor) {
        // If the clicked actor is part of the selection, delete all selected actors
        const selectedIds = selected.map((a) => a.id);
        if (selectedIds.includes(actor.id)) {
          dispatch(deleteActors(selFor(selectedIds)));
        } else {
          dispatch(deleteActors(selFor([actor.id])));
        }
      }
    }
    if (selectedToolId === TOOLS.CREATE_CHARACTER) {
      const newCharacterId = makeId("character");
      const action = createCharacter(newCharacterId);
      dispatch(action);
      dispatch(
        createActors(world.id, stage.id, [
          { character: action.values as Character, initialValues: { position: { x, y } } },
        ]),
      );
      dispatch(paintCharacterAppearance(newCharacterId, "idle"));
    }
  };

  // Note: In this handler, the mouse cursor may be outside the stage
  const onMouseUp = useRef<(event: MouseEvent) => void>();
  onMouseUp.current = (event: MouseEvent) => {
    onMouseMove.current?.(event);

    mouse.current = { isDown: false, visited: {}, lastPosition: null };

    if (selectionRect) {
      const selectedActors: Actor[] = [];
      const [minLeft, maxLeft] = [selectionRect.start.left, selectionRect.end.left].sort(
        (a, b) => a - b,
      );
      const [minTop, maxTop] = [selectionRect.start.top, selectionRect.end.top].sort(
        (a, b) => a - b,
      );
      // Y-up, 1-indexed: convert screen rows (top-down, 0-indexed) to internal
      // y (bottom-up, 1-indexed). The larger screen-top maps to the smaller
      // internal y, so swap min/max on the Y axis.
      const min = {
        x: Math.floor(minLeft / STAGE_CELL_SIZE / scale) + 1,
        y: stageHeight - Math.floor(maxTop / STAGE_CELL_SIZE / scale),
      };
      const max = {
        x: Math.floor(maxLeft / STAGE_CELL_SIZE / scale) + 1,
        y: stageHeight - Math.floor(minTop / STAGE_CELL_SIZE / scale),
      };
      for (const actor of Object.values(stage.actors)) {
        if (
          actor.position.x >= min.x &&
          actor.position.x <= max.x &&
          actor.position.y >= min.y &&
          actor.position.y <= max.y
        ) {
          selectedActors.push(actor);
        }
      }
      const characterId =
        selectedActors.length &&
        selectedActors.every((a) => a.characterId === selectedActors[0].characterId)
          ? selectedActors[0].characterId
          : null;
      dispatch(select(characterId, selFor(selectedActors.map((a) => a.id))));
      setSelectionRect(null);
    }
    if (!event.shiftKey && !event.defaultPrevented) {
      if (
        TOOLS.TRASH === selectedToolId ||
        TOOLS.STAMP === selectedToolId ||
        TOOLS.RECORD === selectedToolId ||
        TOOLS.PAINT === selectedToolId ||
        TOOLS.CREATE_CHARACTER === selectedToolId ||
        TOOLS.IGNORE_SQUARE === selectedToolId
      ) {
        dispatch(selectToolId(TOOLS.POINTER));
      }
    }
  };

  const onRightClickStage = (event: React.MouseEvent) => {
    event.preventDefault();
    if (selectedToolId !== TOOLS.POINTER) {
      dispatch(selectToolId(TOOLS.POINTER));
    }
  };

  const onSelectActor = (actor: Actor) => {
    if (selectedToolId === TOOLS.POINTER) {
      dispatch(select(actor.characterId, selFor([actor.id])));
    }
  };

  const onPopoverSelectActor = (actor: Actor) => {
    if (!actorSelectionPopover) return;

    const { toolId } = actorSelectionPopover;

    switch (toolId) {
      case TOOLS.PAINT:
        dispatch(paintCharacterAppearance(actor.characterId, actor.appearance));
        dispatch(selectToolId(TOOLS.POINTER));
        break;
      case TOOLS.STAMP:
        dispatch(selectToolItem(selFor([actor.id])));
        break;
      case TOOLS.RECORD:
        dispatch(setupRecordingForActor({ characterId: actor.characterId, actor }));
        dispatch(selectToolId(TOOLS.POINTER));
        break;
      case TOOLS.ADD_CLICK_CONDITION:
        dispatch(
          upsertRecordingCondition({
            key: makeId("condition"),
            left: { globalId: "click" },
            right: { constant: actor.id },
            comparator: "=",
            enabled: true,
          }),
        );
        dispatch(selectToolId(TOOLS.POINTER));
        break;
      case TOOLS.TRASH:
        dispatch(deleteActors(selFor([actor.id])));
        break;
      case TOOLS.POINTER:
      default:
        dispatch(select(actor.characterId, selFor([actor.id])));
        el.current?.focus();
        break;
    }

    setActorSelectionPopover(null);
  };

  const onPopoverClose = () => {
    setActorSelectionPopover(null);
  };

  const onStartDoorDestDrag = (
    actorId: string,
    sourceStageId: string,
    initial: Position,
    event: React.MouseEvent,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    // The destination must land on THIS stage (the one being rendered)
    // regardless of which stage hosts the source door actor.
    const clampToStage = (p: Position): Position => ({
      x: Math.max(1, Math.min(stageWidth, p.x)),
      y: Math.max(1, Math.min(stageHeight, p.y)),
    });

    setDoorDestDrag({ actorId, position: initial });

    const removeListeners = addInteractionListeners({
      mousemove: (e) => {
        const position = getPositionForEvent(e);
        if (position) {
          setDoorDestDrag({ actorId, position: clampToStage(position) });
        }
      },
      mouseup: (e) => {
        removeListeners();
        const position = getPositionForEvent(e);
        if (position) {
          const next = clampToStage(position);
          // Important: target the stage that owns the source door actor — for
          // incoming cross-stage doors this is NOT the stage we're rendering.
          dispatch(
            changeActors(buildActorSelection(world.id, sourceStageId, [actorId]), {
              variableValues: {
                [DOOR_VARIABLE_IDS.destinationX]: String(next.x),
                [DOOR_VARIABLE_IDS.destinationY]: String(next.y),
              },
            }),
          );
        }
        setDoorDestDrag(null);
      },
    });
  };

  const renderDoorDestinations = () => {
    if (playback.running) return null;

    const nodes: React.ReactNode[] = [];

    // Local-source: destinations for selected door actors on THIS stage.
    // These are interactive — users can drag them to edit X/Y.
    for (const actor of selected) {
      const character = characters[actor.characterId];
      if (character?.kind !== "door") continue;

      const destXRaw = getVariableValue(actor, character, DOOR_VARIABLE_IDS.destinationX, "=");
      const destYRaw = getVariableValue(actor, character, DOOR_VARIABLE_IDS.destinationY, "=");
      const destStageId =
        getVariableValue(actor, character, DOOR_VARIABLE_IDS.destinationStage, "=") ?? "";

      const destX = Number(destXRaw);
      const destY = Number(destYRaw);
      if (!Number.isFinite(destX) || !Number.isFinite(destY)) continue;

      // Only draw the destination box when it points to this stage.
      if (destStageId && destStageId !== stage.id) continue;

      const active = doorDestDrag?.actorId === actor.id;
      const pos = active ? doorDestDrag!.position : { x: destX, y: destY };

      nodes.push(
        <div
          key={`door-dest-${actor.id}`}
          className="door-destination-box"
          onMouseDown={(e) => onStartDoorDestDrag(actor.id, stage.id, { x: destX, y: destY }, e)}
          style={{
            position: "absolute",
            left: (pos.x - 1) * STAGE_CELL_SIZE,
            // Y-up, 1-indexed: position from the bottom of the stage container.
            bottom: (pos.y - 1) * STAGE_CELL_SIZE,
            width: STAGE_CELL_SIZE,
            height: STAGE_CELL_SIZE,
            border: "2px dashed #ff9500",
            boxShadow: "0 0 0 1px rgba(255,149,0,0.4) inset",
            backgroundColor: active ? "rgba(255,149,0,0.15)" : "transparent",
            cursor: "grab",
            pointerEvents: "auto",
            zIndex: 999,
          }}
        />,
      );
    }

    // Remote-source: destinations for doors on OTHER stages that point here.
    // Always visible (not gated on selection) and draggable — dispatching
    // edits to the source stage id so the source door's variables move.
    for (const incoming of doorsPointingHere ?? []) {
      const active = doorDestDrag?.actorId === incoming.sourceActorId;
      const pos = active ? doorDestDrag!.position : { x: incoming.destX, y: incoming.destY };
      nodes.push(
        <div
          key={`door-dest-incoming-${incoming.sourceStageId}-${incoming.sourceActorId}`}
          className="door-destination-box door-destination-box-incoming"
          title="Destination of a door on another stage"
          onMouseDown={(e) =>
            onStartDoorDestDrag(
              incoming.sourceActorId,
              incoming.sourceStageId,
              { x: incoming.destX, y: incoming.destY },
              e,
            )
          }
          style={{
            position: "absolute",
            left: (pos.x - 1) * STAGE_CELL_SIZE,
            // Y-up, 1-indexed: position from the bottom of the stage container.
            bottom: (pos.y - 1) * STAGE_CELL_SIZE,
            width: STAGE_CELL_SIZE,
            height: STAGE_CELL_SIZE,
            border: "2px dashed #ff9500",
            boxShadow: "0 0 0 1px rgba(255,149,0,0.4) inset",
            backgroundColor: active ? "rgba(255,149,0,0.15)" : "transparent",
            cursor: "grab",
            pointerEvents: "auto",
            zIndex: 998,
          }}
        />,
      );
    }

    return nodes;
  };

  const renderRecordingExtent = () => {
    const width = stageWidth;
    const height = stageHeight;
    if (!recordingExtent) {
      return [];
    }

    const components: React.ReactNode[] = [];
    const { xmin, xmax, ymin, ymax } = recordingExtent;

    // add the dark squares. Y-up, 1-indexed: ymin is the bottommost row of
    // the extent, ymax the topmost; the visual area covered by a mask is
    // (propYmax - propYmin) cells wide/tall, i.e. exclusive at the top.
    components.push(
      <RecordingMaskSprite
        key={`mask-top`}
        xmin={1}
        xmax={width + 1}
        ymin={ymax + 1}
        ymax={height + 1}
      />,
      <RecordingMaskSprite key={`mask-bottom`} xmin={1} xmax={width + 1} ymin={1} ymax={ymin} />,
      <RecordingMaskSprite key={`mask-left`} xmin={1} xmax={xmin} ymin={ymin} ymax={ymax + 1} />,
      <RecordingMaskSprite
        key={`mask-right`}
        xmin={xmax + 1}
        xmax={width + 1}
        ymin={ymin}
        ymax={ymax + 1}
      />,
    );

    // add the ignored squares
    if (interactionMode === "selectable") {
      extentIgnoredPositions(recordingExtent)
        .filter(({ x, y }) => x >= xmin && x <= xmax && y >= ymin && y <= ymax)
        .forEach(({ x, y }) => {
          components.push(<RecordingIgnoredSprite x={x} y={y} key={`ignored-${x}-${y}`} />);
        });
    }

    // add square status overlay if provided
    if (evaluatedSquares && evaluatedSquares.length > 0) {
      components.push(
        <RecordingSquareStatus
          key="square-status"
          squares={evaluatedSquares}
          extentXMin={xmin}
          extentYMin={ymin}
        />,
      );
    }

    // add the handles. Y-up: ymin is the bottom row, ymax is the top row.
    const handles: { [side in RecordingHandleSide]: [number, number] } = {
      top: [xmin + (xmax - xmin) / 2.0, ymax + 1],
      bottom: [xmin + (xmax - xmin) / 2.0, ymin - 1],
      left: [xmin - 1, ymin + (ymax - ymin) / 2.0],
      right: [xmax + 1, ymin + (ymax - ymin) / 2.0],
    };
    for (const side of Object.keys(handles) as RecordingHandleSide[]) {
      const [x, y] = handles[side];
      components.push(
        <RecordingHandle key={side} side={side} position={{ x, y }} onDrag={onUpdateHandle} />,
      );
    }

    return components;
  };

  if (!stage) {
    return (
      <div style={style} ref={(el) => (scrollEl.current = el)} className="stage-scroll-wrap" />
    );
  }

  const rawBackground = getStageBackground(stage);
  const backgroundValue = rawBackground.includes("/Layer0_2.png")
    ? `url(${new URL(`../../img/backgrounds/Layer0_2.png`, import.meta.url).href})`
    : rawBackground;

  const isImageBackground = backgroundValue?.includes("url(");
  // For image backgrounds, optionally apply a white overlay to dim the image.
  // For solid color backgrounds the overlay is never applied.
  const applyFade = isImageBackground && stage.backgroundFade !== false;
  // The grid is for building: hide it while the game plays, and always in the
  // player (the only read-only stage that isn't part of a rule recording).
  const isPlayerStage = interactionMode === "selectable" && !recordingExtent;
  const showGrid = !playback.running && !isPlayerStage;
  const backgroundCSS = [
    showGrid
      ? `url('${new URL("../../img/board-grid.png", import.meta.url).href}') top left / ${STAGE_CELL_SIZE}px`
      : null,
    applyFade ? `linear-gradient(rgba(255,255,255,0.25), rgba(255,255,255,0.25))` : null,
    `${backgroundValue}${isImageBackground ? " 50% 50% / cover" : ""}`,
  ]
    .filter(Boolean)
    .join(",\n    ");

  const renderActor = (actor: Actor) => {
    const character = characters[actor.characterId];

    // Detect when an actor wraps off one end of the stage to the other
    // and skip transition animation to prevent sliding across the whole screen
    const lastPosition = lastActorPositions.current[actor.id] || {
      x: Number.NaN,
      y: Number.NaN,
    };
    const didWrap =
      Math.abs(lastPosition.x - actor.position.x) > 6 ||
      Math.abs(lastPosition.y - actor.position.y) > 6;
    lastActorPositions.current[actor.id] = Object.assign({}, actor.position);

    const draggable = interactionMode === "full" && !NON_DRAGGING_TOOLS.includes(selectedToolId);
    const animationStyle = actor.animationStyle || "linear";
    const zIndex = characterZOrder.indexOf(actor.characterId);
    return (
      <ActorSprite
        key={`${actor.id}::${stoppedGeneration}`}
        selected={selected.includes(actor)}
        zIndex={zIndex >= 0 ? zIndex : undefined}
        onMouseUp={(event) => onMouseUpActor(actor, event)}
        onDoubleClick={() => onSelectActor(actor)}
        transitionDuration={
          animationStyle === "linear" ? playback.speed / (actor.frameCount || 1) : 0
        }
        skipTransition={didWrap}
        character={character}
        actor={actor}
        dragActorIds={
          draggable && !playback.running
            ? selected.includes(actor)
              ? selected.map((a) => a.id)
              : [actor.id]
            : undefined
        }
        onStartDrag={draggable && !playback.running ? onStartSpriteDrag : undefined}
      />
    );
  };

  return (
    <div
      style={style}
      ref={(e) => (scrollEl.current = e)}
      data-stage-wrap-id={world.id}
      data-stage-zoom={scale}
      className={`stage-scroll-wrap tool-supported running-${playback.running}${
        playback.running || animatingTick ? " animations-enabled" : ""
      }`}
    >
      <div
        ref={(e) => (el.current = e)}
        style={
          {
            top,
            left,
            width: stageWidth * STAGE_CELL_SIZE,
            height: stageHeight * STAGE_CELL_SIZE,
            overflow: recordingExtent ? "visible" : "hidden",
            zoom: scale,
            "--outline-width": `${2.0 / scale}px`,
          } as CSSProperties
        }
        className="stage"
        onDragOver={onDragOver}
        onDrop={onDrop}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onContextMenu={onRightClickStage}
        onMouseDown={onMouseDown}
        tabIndex={0}
      >
        <div
          className="background"
          style={{
            position: "absolute",
            width: stageWidth * STAGE_CELL_SIZE,
            height: stageHeight * STAGE_CELL_SIZE,
            background: backgroundCSS,
            pointerEvents: "none",
            filter: applyFade ? "brightness(0.9) saturate(0.9)" : undefined,
          }}
        />

        {sortActorsByZOrder(Object.values(stage.actors), characterZOrder).map(renderActor)}

        {renderDoorDestinations()}

        {recordingExtent ? renderRecordingExtent() : []}
      </div>
      {selectionRect ? (
        <div
          className="stage-selection-box"
          style={{
            position: "absolute",
            zIndex: 1,
            left: centeringOffset.left + Math.min(selectionRect.start.left, selectionRect.end.left),
            top: centeringOffset.top + Math.min(selectionRect.start.top, selectionRect.end.top),
            width:
              Math.max(selectionRect.start.left, selectionRect.end.left) -
              Math.min(selectionRect.start.left, selectionRect.end.left),
            height:
              Math.max(selectionRect.start.top, selectionRect.end.top) -
              Math.min(selectionRect.start.top, selectionRect.end.top),
          }}
        />
      ) : null}
      {spriteDrag &&
        createPortal(
          <SpriteDragPreview spriteDrag={spriteDrag} characters={characters} scale={scale} />,
          document.body,
        )}
      {actorSelectionPopover && (
        <ActorSelectionPopover
          actors={actorSelectionPopover.actors}
          characters={characters}
          position={actorSelectionPopover.position}
          onSelect={onPopoverSelectActor}
          onClose={onPopoverClose}
          onStartDrag={
            actorSelectionPopover.toolId === TOOLS.POINTER ? onStartSpriteDrag : undefined
          }
        />
      )}
    </div>
  );
};

export default Stage;
